import React from "react";

import { faPlus } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import { Button } from "~/ui";
import ArrowDropdown from "~/ui/ArrowDropdown";
import { Input } from "~/ui/legacyFellowIndex";

import { DeleteTaskButton, getTimeText, Header, TaskSummary, ValueDropdownItem } from "./Tasks";
import type { TaskType } from "./types";

import stylesTimeSaver from "~/stream/StreamSettings/Automations/TimeSaver.module.css";

import styles from "./MeetingReminders.module.css";

const MAX_NUM_REMINDERS = 5;
const REMINDER_VALUES = [-15, -30, -60, -120, -240, -1440, -2880, -4320] as const; // minutes
type ReminderValueType = (typeof REMINDER_VALUES)[number];

interface PromptNotificationProps {
	id?: string;
	streamId: string;
	automationId: string;
	message?: string;
	delta?: ReminderValueType;
	onBack: () => void;
	onHome: () => void;
	lastOne: boolean;
}

function PromptNotification({
	id,
	streamId,
	automationId,
	message = "",
	delta = REMINDER_VALUES[1],
	onBack,
	onHome,
	lastOne,
}: PromptNotificationProps) {
	const [deltaValue, setDeltaValue] = React.useState<ReminderValueType>(delta); // minutes
	const [messageValue, setMessageValue] = React.useState<string>(message);
	const onMessageChange = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => setMessageValue(e.target.value),
		[],
	);

	const input = React.useMemo(
		() => ({
			streamId,
			automationId,
			task: {
				id,
				delta: deltaValue,
				data: { message: messageValue.trim() },
			},
		}),
		[automationId, deltaValue, id, messageValue, streamId],
	);

	return (
		<>
			<Header
				name="Reminder"
				backName="Add to Section Reminders"
				onHome={onHome}
				onBack={onBack}
				input={input}
				showButtons
			/>

			<div className={cx(styles.box, stylesTimeSaver.box)}>
				<div className={cx(styles.row, stylesTimeSaver.row, styles.tight)}>
					<label className={stylesTimeSaver.label}>Send reminder:</label>
					<ArrowDropdown
						compact
						heading="Send reminder"
						text={getTimeText(deltaValue)}
						className={stylesTimeSaver.dropdown}
						textClassName={stylesTimeSaver.dropdownText}
						popupClassName={stylesTimeSaver.dropdownPopup}
					>
						{REMINDER_VALUES.map(value => (
							<ValueDropdownItem key={value} value={value} onClick={setDeltaValue}>
								{getTimeText(value)}
							</ValueDropdownItem>
						))}
					</ArrowDropdown>
				</div>

				<label className={cx(styles.row, stylesTimeSaver.row, styles.tight, stylesTimeSaver.label)}>
					Custom message:
				</label>
				<div className={cx(styles.row, stylesTimeSaver.row)}>
					<Input
						className={styles.input}
						value={messageValue}
						onChange={onMessageChange}
						placeholder="Set a custom message to go with your reminder…"
						maxLength={255}
						name="prompt_notification_message"
					/>
				</div>
			</div>

			{id && !lastOne && (
				<div className={styles.alignedRightContainer}>
					<DeleteTaskButton taskId={id} name="reminder" callback={onBack} />
				</div>
			)}
		</>
	);
}

interface PromptNotificationsProps {
	streamId: string;
	automationId: string;
	onBack: () => void;
	tasks: readonly TaskType[];
}

export function PromptNotifications({ streamId, automationId, onBack, tasks }: PromptNotificationsProps) {
	const numTasks = tasks.length;

	const [taskConfig, setTaskConfig] = React.useState<string | null>(null); // task.id or "new"
	const clearTaskConfig = React.useCallback(() => setTaskConfig(null), []);
	const onNewTaskConfig = React.useCallback(() => setTaskConfig("new"), []);

	if (taskConfig) {
		const commonProps = {
			streamId,
			automationId,
			onHome: onBack,
			onBack: clearTaskConfig,
		};

		if (taskConfig === "new") {
			return <PromptNotification {...commonProps} lastOne={false} />;
		}

		const task = tasks.find(t => t.id === taskConfig);

		if (task) {
			const taskData = {
				id: task.id,
				delta: task.delta as ReminderValueType,
				message: task.data?.message,
			};

			return <PromptNotification {...taskData} {...commonProps} lastOne={tasks.length === 1} />;
		}
	}

	return (
		<>
			<Header name="Add to Section Reminders" onHome={onBack} />
			{tasks.map(task => (
				<TaskSummary key={task.id} task={task} onClick={setTaskConfig} />
			))}
			{numTasks < MAX_NUM_REMINDERS && (
				<Button size="wide" variant="secondary" onClick={onNewTaskConfig} className={styles.newTaskButton}>
					<FontAwesomeIcon icon={faPlus} style={{ marginRight: "0.5ch" }} />
					Add another reminder
				</Button>
			)}
		</>
	);
}
